if (!customElements.get('collection-product-table')) {
  class CollectionProductTable extends HTMLElement {
    constructor() {
      super();
      this.cartItems = new Set();
      this.cartRollLocation = null; // Track the location of rolls in cart
      this.searchTimeout = null;
      this.currentPriceM2Range = null;
      this.outletFilterActive = false;
      this.allProductsLoaded = false;
      this.initialized = false;
    }

    connectedCallback() {
      // Only initialize once and ensure DOM is ready
      if (this.initialized) return;
      this.initialized = true;

      // Get element references
      this.loadMoreButton = document.getElementById('LoadMoreButton');
      this.tableBody = document.getElementById('ProductTableBody');
      this.searchInput = document.getElementById('CollectionTableSearch');
      this.priceM2Filter = document.querySelector('[data-price-m2-filter]');
      this.outletSelect = document.getElementById('OutletFilterSelect');

      this.init();
    }

    init() {
      // Ensure all product rows are visible initially
      this.showAllProductRows();

      this.fetchCartItems();
      this.setupEventListeners();
      this.setupBatchNumberLinks();
      this.setupAddToCartForms();

      // Listen for cart updates using Shopify's pub/sub system
      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
      }

      // Handle browser back/forward navigation (bfcache)
      // When user navigates back, refresh cart state to show correct button states
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) {
          // Page was restored from bfcache - refresh cart data
          this.fetchCartItems();
        }
      });

      // Apply filters from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const searchTerm = urlParams.get('q');
      const priceM2 = urlParams.get('price_m2');
      const outletParam = urlParams.get('outlet');

      // Set search input value
      if (searchTerm && this.searchInput) {
        this.searchInput.value = searchTerm;
        this.updateSearchBadge(searchTerm);
      }

      // Set price filter value
      if (priceM2 && this.priceM2Filter) {
        this.priceM2Filter.value = priceM2;
        this.currentPriceM2Range = priceM2;
        this.updatePriceM2Badge(priceM2);
      }

      // Set outlet filter state
      if (outletParam === '1' && this.outletSelect) {
        this.outletFilterActive = true;
        this.updateOutletToggleState();
        this.updateOutletBadge(true);
      }

      // Apply all client filters if any are set
      if (searchTerm || priceM2 || this.outletFilterActive) {
        // If there's a search term or outlet filter, load all pages first so we can filter everything
        if (searchTerm || this.outletFilterActive) {
          this.loadAllRemainingProducts().then(() => {
            this.applyClientFilters();
          });
        } else {
          this.applyClientFilters();
        }
      }
    }

    showAllProductRows() {
      const productRows = this.querySelectorAll('.product-row');
      productRows.forEach(row => {
        row.style.display = '';
      });
    }

    setupEventListeners() {
      // Load more button
      if (this.loadMoreButton) {
        this.loadMoreButton.addEventListener('click', this.loadMoreProducts.bind(this));
      }

      // Search input - client-side only, prevent form submit on Enter
      if (this.searchInput) {
        this.searchInput.addEventListener('input', this.handleSearch.bind(this));
        this.searchInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') e.preventDefault();
        });
      }

      // Price per m² filter
      if (this.priceM2Filter) {
        this.priceM2Filter.addEventListener('change', this.handlePriceM2Filter.bind(this));
      }

      // Clear search badge button
      const clearSearchButton = document.getElementById('ClearSearchQuery');
      if (clearSearchButton) {
        clearSearchButton.addEventListener('click', this.handleClearSearch.bind(this));
      }

      // Clear price m² badge button
      const clearPriceM2Button = document.getElementById('ClearPriceM2Filter');
      if (clearPriceM2Button) {
        clearPriceM2Button.addEventListener('click', this.handleClearPriceM2Filter.bind(this));
      }

      // Outlet filter dropdown
      if (this.outletSelect) {
        this.outletSelect.addEventListener('change', (event) => {
          this.setOutletFilter(event.target.value === '1');
        });
      }

      // Clear outlet badge button (delegated: badge markup is replaced on AJAX updates)
      document.addEventListener('click', (event) => {
        if (event.target.closest('#ClearOutletFilter')) {
          this.handleClearOutletFilter();
        }
      });

      // Facet filters - handle form submission with AJAX
      const facetForm = document.getElementById('FacetFiltersForm');
      if (facetForm) {
        facetForm.addEventListener('submit', (event) => {
          event.preventDefault();
          this.handleFilterSubmit(facetForm);
        });

        // Add instant filtering on select change
        const filterSelects = facetForm.querySelectorAll('select[name^="filter."]');
        filterSelects.forEach(select => {
          select.addEventListener('change', () => {
            this.handleFilterSubmit(facetForm);
          });
        });

        // Add instant filtering on price range inputs (with debounce)
        const priceInputs = facetForm.querySelectorAll('input[type="number"]');
        let priceTimeout;
        priceInputs.forEach(input => {
          input.addEventListener('input', () => {
            clearTimeout(priceTimeout);
            priceTimeout = setTimeout(() => {
              this.handleFilterSubmit(facetForm);
            }, 800); // Debounce 800ms for typing
          });
        });
      }

      // Products per page dropdown - handle change with AJAX
      const perPageSelect = document.getElementById('PerPage');
      if (perPageSelect) {
        perPageSelect.addEventListener('change', (event) => {
          this.handlePerPageChange(event.target.value);
        });
      }

      // Clear all button - handle with AJAX
      const clearButtons = document.querySelectorAll('.active-facets a');
      clearButtons.forEach(button => {
        if (button.textContent.toLowerCase().includes('clear') || button.href.includes(window.location.pathname + '?')) {
          button.addEventListener('click', (event) => {
            event.preventDefault();
            if (button.closest('facet-remove')) {
              // Individual filter removal
              this.updateCollectionWithUrl(button.href);
            } else {
              // Clear all filters
              this.handleClearFilters();
            }
          });
        }
      });
    }

    handleClearFilters() {
      const currentUrl = new URL(window.location.href);
      const baseUrl = currentUrl.pathname;

      const sortBy = currentUrl.searchParams.get('sort_by');
      const priceM2 = currentUrl.searchParams.get('price_m2');

      const params = new URLSearchParams();
      if (sortBy) params.set('sort_by', sortBy);

      const newUrl = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

      // Clear search input too
      if (this.searchInput) this.searchInput.value = '';
      this.updateSearchBadge('');

      // Clear outlet filter too
      this.outletFilterActive = false;
      this.updateOutletToggleState();
      this.updateOutletBadge(false);

      this.updateCollectionWithUrl(newUrl).then(() => {
        if (priceM2) {
          const updatedUrl = new URL(window.location.href);
          updatedUrl.searchParams.set('price_m2', priceM2);
          this.updatePriceM2Badge(priceM2);
          window.history.pushState({}, '', updatedUrl.toString());
          this.applyClientFilters();
        }
      });
    }

    setupBatchNumberLinks() {
      const batchLinks = this.querySelectorAll('button[data-batch-number]');
      batchLinks.forEach(link => {
        link.addEventListener('click', this.handleBatchNumberClick.bind(this));
      });
    }

    setupAddToCartForms() {
      const forms = this.querySelectorAll('form[data-type="add-to-cart-form"]');
      forms.forEach(form => {
        form.addEventListener('submit', this.handleAddToCart.bind(this));
      });
    }

    handleBatchNumberClick(event) {
      const button = event.currentTarget;
      const batchNumber = button.dataset.batchNumber;

      if (!batchNumber) return;

      // Clear existing filters and apply batch number filter
      const currentUrl = new URL(window.location.href);
      const baseUrl = currentUrl.pathname;

      // Create new URL with only batch number filter
      const newUrl = `${baseUrl}?filter.p.m.product.batch_number=${encodeURIComponent(batchNumber)}`;

      // Navigate to filtered view
      window.location.href = newUrl;
    }

    handleFilterSubmit(form) {
      const formData = new FormData(form);
      const currentUrl = new URL(window.location.href);

      // Build URL with all filter parameters
      const searchParams = new URLSearchParams();

      // Add all form fields to search params (excluding client-side filters)
      for (const [key, value] of formData.entries()) {
        // Skip empty values and skip client-side filters (q = search, price_per_m2 = price filter)
        if (key !== 'q' && key !== 'price_per_m2' && value && value.trim() !== '') {
          searchParams.append(key, value);
        }
      }

      // Preserve sort_by if it exists and not in form
      if (currentUrl.searchParams.has('sort_by') && !searchParams.has('sort_by')) {
        searchParams.set('sort_by', currentUrl.searchParams.get('sort_by'));
      }

      // Get the search query to reapply after server update
      const searchQuery = currentUrl.searchParams.get('q');

      // Build new URL (without 'q' for server request)
      const baseUrl = currentUrl.pathname;
      const newUrl = searchParams.toString() ? `${baseUrl}?${searchParams.toString()}` : baseUrl;

      // Get the price filter to reapply after server update
      const priceM2 = currentUrl.searchParams.get('price_m2');

      // Update collection via AJAX
      this.updateCollectionWithUrl(newUrl).then(() => {
        // Update URL to include client-side filter parameters
        const updatedUrl = new URL(window.location.href);
        if (searchQuery) {
          updatedUrl.searchParams.set('q', searchQuery);
          this.updateSearchBadge(searchQuery);
        }
        if (priceM2) {
          updatedUrl.searchParams.set('price_m2', priceM2);
          this.updatePriceM2Badge(priceM2);
        }
        if (this.outletFilterActive) {
          updatedUrl.searchParams.set('outlet', '1');
          this.updateOutletBadge(true);
        }
        window.history.pushState({}, '', updatedUrl.toString());

        // Apply all client-side filters
        if (searchQuery || priceM2 || this.outletFilterActive) {
          this.applyClientFilters();
        }
      });
    }

    handlePerPageChange(perPageValue) {
      const currentUrl = new URL(window.location.href);

      if (perPageValue) {
        currentUrl.searchParams.set('per_page', perPageValue);
      } else {
        currentUrl.searchParams.delete('per_page');
      }

      // Reset to page 1 when changing per_page
      currentUrl.searchParams.delete('page');

      // Get client-side filters to reapply after server update
      const searchQuery = currentUrl.searchParams.get('q');
      const priceM2 = currentUrl.searchParams.get('price_m2');

      // Remove client-side params from URL for server request
      const serverUrl = new URL(currentUrl.toString());
      serverUrl.searchParams.delete('q');
      serverUrl.searchParams.delete('price_m2');
      serverUrl.searchParams.delete('outlet');

      // Update collection via AJAX
      this.updateCollectionWithUrl(serverUrl.toString()).then(() => {
        // Reapply client-side filters
        const updatedUrl = new URL(window.location.href);
        if (searchQuery) {
          updatedUrl.searchParams.set('q', searchQuery);
          this.updateSearchBadge(searchQuery);
        }
        if (priceM2) {
          updatedUrl.searchParams.set('price_m2', priceM2);
          this.updatePriceM2Badge(priceM2);
        }
        if (this.outletFilterActive) {
          updatedUrl.searchParams.set('outlet', '1');
          this.updateOutletBadge(true);
        }
        window.history.pushState({}, '', updatedUrl.toString());
        if (searchQuery || priceM2 || this.outletFilterActive) {
          this.applyClientFilters();
        }
      });
    }

    handleSearch(event) {
      clearTimeout(this.searchTimeout);
      const searchValue = event.target.value.trim();

      this.searchTimeout = setTimeout(async () => {
        // Update URL
        const currentUrl = new URL(window.location.href);
        if (searchValue) {
          currentUrl.searchParams.set('q', searchValue);
        } else {
          currentUrl.searchParams.delete('q');
        }
        window.history.pushState({}, '', currentUrl.toString());

        // Update search badge
        this.updateSearchBadge(searchValue);

        // Load all products first if not already loaded, then filter client-side
        if (searchValue) {
          if (!this.allProductsLoaded) {
            await this.loadAllRemainingProducts();
          }
        }

        this.applyClientFilters();
      }, 300);
    }

    handlePriceM2Filter(event) {
      const value = event.target.value;
      this.currentPriceM2Range = value || null;

      // Update URL to reflect price filter
      const currentUrl = new URL(window.location.href);
      if (value) {
        currentUrl.searchParams.set('price_m2', value);
      } else {
        currentUrl.searchParams.delete('price_m2');
      }
      window.history.pushState({}, '', currentUrl.toString());

      // Update price badge
      this.updatePriceM2Badge(value);

      // Apply all client filters
      this.applyClientFilters();
    }

    updatePriceM2Badge(priceRange) {
      const badge = document.querySelector('.price-m2-badge');
      const badgeText = document.querySelector('.price-m2-text');
      const activeFacets = document.querySelector('.active-facets');

      if (badge && badgeText) {
        if (priceRange) {
          // Format the display text
          let displayText = priceRange;
          if (priceRange.endsWith('+')) {
            displayText = `€${priceRange.replace('+', '')}+ / m²`;
          } else {
            const [min, max] = priceRange.split('-');
            displayText = `€${min} - €${max} / m²`;
          }
          badgeText.textContent = displayText;
          badge.style.display = 'block';

          // Show the active facets container if hidden
          if (activeFacets) {
            activeFacets.classList.remove('hidden');
          }
        } else {
          badge.style.display = 'none';

          // Check if there are other active filters
          const hasOtherFilters = activeFacets &&
                                  activeFacets.querySelectorAll('.active-facets__button-wrapper').length > 0;
          const hasSearchBadge = document.querySelector('.search-query-badge')?.style.display !== 'none';
          const outletBadge = document.querySelector('.outlet-filter-badge');
          const hasOutletBadge = outletBadge && outletBadge.style.display !== 'none';

          // Hide active facets container if no other filters
          if (activeFacets && !hasOtherFilters && !hasSearchBadge && !hasOutletBadge) {
            activeFacets.classList.add('hidden');
          }
        }
      }
    }

    applyClientFilters() {
      const searchTerm = this.searchInput?.value.trim().toLowerCase() || '';
      const productRows = this.querySelectorAll('.product-row');
      let visibleCount = 0;

      productRows.forEach(row => {
        let matchesSearch = true;
        let matchesPrice = true;
        let matchesOutlet = true;

        // Outlet filter: only show rolls marked as outlet
        if (this.outletFilterActive) {
          matchesOutlet = row.dataset.outlet === 'true';
        }

        // Client-side search on batch number, roll number, product title
        if (searchTerm) {
          const productTitle = (row.dataset.productTitle || '').toLowerCase();
          const batchNumber = (row.dataset.batchNumber || '').toLowerCase();
          const rollNumber = (row.dataset.rollNumber || '').toLowerCase();

          matchesSearch = productTitle.includes(searchTerm) ||
                         batchNumber.includes(searchTerm) ||
                         rollNumber.includes(searchTerm);
        }

        // Check price per m² filter
        if (this.currentPriceM2Range) {
          const pricePerM2 = parseFloat(row.dataset.pricePerM2);

          if (!isNaN(pricePerM2)) {
            // Price is in cents, convert to euros for comparison
            const priceInEuros = pricePerM2 / 100;

            if (this.currentPriceM2Range.endsWith('+')) {
              const minPrice = parseFloat(this.currentPriceM2Range.replace('+', ''));
              matchesPrice = priceInEuros >= minPrice;
            } else {
              const [minPrice, maxPrice] = this.currentPriceM2Range.split('-').map(parseFloat);
              matchesPrice = priceInEuros >= minPrice && priceInEuros < maxPrice;
            }
          } else {
            // No price per m² data (e.g. accessories) - keep visible, don't filter
            matchesPrice = true;
          }
        }

        // Show row only if it matches all filters
        if (matchesSearch && matchesPrice && matchesOutlet) {
          row.style.display = '';
          visibleCount++;
        } else {
          row.style.display = 'none';
        }
      });

      // Show/hide empty state
      this.toggleEmptyState(visibleCount === 0 && (searchTerm || this.currentPriceM2Range || this.outletFilterActive));

      // Update product count
      this.updateProductCount(visibleCount, productRows.length);
    }

    handleClearOutletFilter() {
      this.setOutletFilter(false);
    }

    async setOutletFilter(active) {
      this.outletFilterActive = active;

      // Update URL to reflect outlet filter
      const currentUrl = new URL(window.location.href);
      if (active) {
        currentUrl.searchParams.set('outlet', '1');
      } else {
        currentUrl.searchParams.delete('outlet');
      }
      window.history.pushState({}, '', currentUrl.toString());

      this.updateOutletToggleState();
      this.updateOutletBadge(active);

      // Load every page first so outlet rolls beyond the current page show up
      if (active && !this.allProductsLoaded) {
        await this.loadAllRemainingProducts();
      }

      this.applyClientFilters();
    }

    updateOutletToggleState() {
      // Keep the dropdown in sync when the filter is set/cleared elsewhere (badge, clear all, URL)
      if (this.outletSelect) {
        this.outletSelect.value = this.outletFilterActive ? '1' : '';
      }
    }

    updateOutletBadge(active) {
      const badge = document.querySelector('.outlet-filter-badge');
      const activeFacets = document.querySelector('.active-facets');

      if (!badge) return;

      if (active) {
        badge.style.display = 'block';

        // Show the active facets container if hidden
        if (activeFacets) {
          activeFacets.classList.remove('hidden');
        }
      } else {
        badge.style.display = 'none';

        // Check if there are other active filters
        const hasOtherFilters = activeFacets &&
                                activeFacets.querySelectorAll('.active-facets__button-wrapper').length > 0;
        const searchBadge = document.querySelector('.search-query-badge');
        const hasSearchBadge = searchBadge && searchBadge.style.display !== 'none';
        const priceBadge = document.querySelector('.price-m2-badge');
        const hasPriceBadge = priceBadge && priceBadge.style.display !== 'none';

        // Hide active facets container if no other filters
        if (activeFacets && !hasOtherFilters && !hasSearchBadge && !hasPriceBadge) {
          activeFacets.classList.add('hidden');
        }
      }
    }

    handleClearSearch() {
      // Clear the search input
      if (this.searchInput) {
        this.searchInput.value = '';
      }

      // Update URL
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('q');
      window.history.pushState({}, '', currentUrl.toString());

      // Hide search badge
      this.updateSearchBadge('');

      // Show all rows and reapply remaining filters
      this.showAllProductRows();
      this.applyClientFilters();
    }

    handleClearPriceM2Filter() {
      // Clear the price filter
      if (this.priceM2Filter) {
        this.priceM2Filter.value = '';
      }
      this.currentPriceM2Range = null;

      // Update URL
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete('price_m2');
      window.history.pushState({}, '', currentUrl.toString());

      // Hide price badge
      this.updatePriceM2Badge('');

      // Apply remaining filters (search if active)
      this.applyClientFilters();
    }

    updateSearchBadge(searchTerm) {
      const badge = document.querySelector('.search-query-badge');
      const badgeText = document.querySelector('.search-query-text');
      const activeFacets = document.querySelector('.active-facets');

      if (badge && badgeText) {
        if (searchTerm) {
          badgeText.textContent = searchTerm;
          badge.style.display = 'block';

          // Show the active facets container if hidden
          if (activeFacets) {
            activeFacets.classList.remove('hidden');
          }
        } else {
          badge.style.display = 'none';

          // Check if there are other active filters
          const hasOtherFilters = activeFacets &&
                                  activeFacets.querySelectorAll('.active-facets__button-wrapper').length > 0;
          const outletBadge = document.querySelector('.outlet-filter-badge');
          const hasOutletBadge = outletBadge && outletBadge.style.display !== 'none';

          // Hide active facets container if no other filters
          if (activeFacets && !hasOtherFilters && !hasOutletBadge) {
            activeFacets.classList.add('hidden');
          }
        }
      }
    }

    toggleEmptyState(show) {
      const emptyState = this.querySelector('.search-empty-state');

      if (emptyState) {
        emptyState.style.display = show ? 'block' : 'none';
      }
    }

    updateProductCount(visibleCount, totalCount) {
      const productCountElement = document.getElementById('ProductCount');
      if (productCountElement) {
        const searchTerm = new URL(window.location.href).searchParams.get('q') || '';
        if (searchTerm) {
          productCountElement.textContent = `${visibleCount} of ${totalCount} products`;
        } else {
          productCountElement.textContent = `${totalCount} product${totalCount !== 1 ? 's' : ''}`;
        }
      }
    }

    async loadMoreProducts() {
      if (!this.loadMoreButton) return;

      const nextUrl = this.loadMoreButton.dataset.nextUrl;
      if (!nextUrl) return;

      this.loadMoreButton.disabled = true;
      this.loadMoreButton.textContent = 'Loading...';

      try {
        const response = await fetch(nextUrl);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Get new rows
        const newRows = doc.querySelectorAll('#ProductTableBody .product-row');

        if (newRows.length > 0 && this.tableBody) {
          newRows.forEach(row => {
            this.tableBody.appendChild(row);
          });

          // Update cart button states for new rows
          this.updateCartButtonStates();
          this.setupBatchNumberLinks();
          this.setupAddToCartForms();
        }

        // Check if there's another page
        const newLoadMoreButton = doc.getElementById('LoadMoreButton');
        if (newLoadMoreButton) {
          this.loadMoreButton.dataset.nextUrl = newLoadMoreButton.dataset.nextUrl;
          this.loadMoreButton.disabled = false;
          this.loadMoreButton.textContent = newLoadMoreButton.textContent;
        } else {
          this.loadMoreButton.remove();
        }

      } catch (error) {
        console.error('Error loading more products:', error);
        this.loadMoreButton.disabled = false;
        this.loadMoreButton.textContent = 'Load More';
      }
    }

    async loadAllRemainingProducts() {
      // Keep loading pages until there are no more
      while (this.loadMoreButton) {
        const nextUrl = this.loadMoreButton.dataset.nextUrl;
        if (!nextUrl) break;

        try {
          const response = await fetch(nextUrl);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');

          // Append new rows
          const newRows = doc.querySelectorAll('#ProductTableBody .product-row');
          if (newRows.length > 0 && this.tableBody) {
            newRows.forEach(row => {
              this.tableBody.appendChild(row);
            });
            this.setupBatchNumberLinks();
            this.setupAddToCartForms();
          }

          // Check if there's another page
          const newLoadMoreButton = doc.getElementById('LoadMoreButton');
          if (newLoadMoreButton) {
            this.loadMoreButton.dataset.nextUrl = newLoadMoreButton.dataset.nextUrl;
          } else {
            this.loadMoreButton.remove();
            this.loadMoreButton = null;
          }
        } catch (error) {
          console.error('Error loading all products:', error);
          break;
        }
      }

      this.allProductsLoaded = true;

      // Update cart button states for all loaded rows
      this.updateCartButtonStates();
    }

    async handleAddToCart(event) {
      event.preventDefault();

      const form = event.target;
      const button = form.querySelector('button[type="submit"]');
      const errorDiv = form.querySelector('.product-error');
      const productRow = form.closest('.product-row');
      const productId = productRow.dataset.productId;
      const variantId = form.querySelector('input[name="id"]').value;
      const productLocation = productRow.dataset.location || '';
      const productType = productRow.dataset.productType || '';

      if (!button || !variantId) return;

      // Check if already in cart
      if (this.cartItems.has(productId)) {
        this.showError(errorDiv, 'This product is already in your cart');
        return;
      }

      // Check if this is a turf roll (not accessory)
      const isTurfRoll = productType !== 'accessory' && productType !== 'accessories' && productLocation;

      // Location validation: rolls from different locations cannot be combined
      if (isTurfRoll && this.cartRollLocation && this.cartRollLocation !== productLocation) {
        this.showLocationError(productLocation);
        return;
      }

      const buttonText = button.querySelector('.add-button-text');
      const buttonAdded = button.querySelector('.add-button-added');

      // Optimistic UI: flip to "added" state before the network round-trip.
      // Rolled back in the catch block if the request fails.
      const previousRollLocation = this.cartRollLocation;
      this.cartItems.add(productId);
      if (isTurfRoll) {
        this.cartRollLocation = productLocation;
      }
      this.saveCartItemsToStorage();
      this.updateButtonState(button, true);
      if (buttonText) buttonText.style.display = 'none';
      if (buttonAdded) buttonAdded.style.display = 'inline';

      try {
        const formData = new FormData(form);

        // Add location as line item property for cart display
        if (productLocation) {
          formData.append('properties[_location]', productLocation);
        }

        // Ask Shopify to render the cart-notification sections in the response
        // so we can show the toast after add-to-cart.
        const cartNotification = document.querySelector('cart-notification');
        if (cartNotification && typeof cartNotification.getSectionsToRender === 'function') {
          const sectionIds = cartNotification.getSectionsToRender().map((s) => s.id).join(',');
          formData.append('sections', sectionIds);
          formData.append('sections_url', window.location.pathname);
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Accept': 'application/javascript',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.description || 'Failed to add to cart');
        }

        const data = await response.json();

        // Publish cart update event
        if (window.PUB_SUB_EVENTS) {
          document.documentElement.dispatchEvent(
            new CustomEvent(window.PUB_SUB_EVENTS.cartUpdate, {
              bubbles: true,
            })
          );
        }

        // Trigger the cart-notification toast
        if (cartNotification && data.sections) {
          cartNotification.renderContents(data);
        }

        // Update cart drawer if it exists
        this.refreshCart();

      } catch (error) {
        console.error('Error adding to cart:', error);

        this.cartItems.delete(productId);
        this.cartRollLocation = previousRollLocation;
        this.saveCartItemsToStorage();
        this.updateButtonState(button, false);
        if (buttonText) buttonText.style.display = '';
        if (buttonAdded) buttonAdded.style.display = '';

        this.showError(errorDiv, error.message);
      }
    }

    showLocationError(newLocation) {
      // Create and show modal with location error
      const existingModal = document.getElementById('location-error-modal');
      if (existingModal) {
        existingModal.remove();
      }

      const modal = document.createElement('div');
      modal.id = 'location-error-modal';
      modal.className = 'fixed inset-0 z-50 flex items-center justify-center p-4';
      modal.innerHTML = `
        <div class="absolute inset-0 bg-black/50" onclick="this.parentElement.remove()"></div>
        <div class="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl p-6">
          <button type="button" onclick="this.closest('#location-error-modal').remove()" class="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 cursor-pointer" aria-label="Close">
            <svg class="size-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
              <svg class="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
              </svg>
            </div>
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">Different Location</h3>
              <p class="text-gray-600 text-sm leading-relaxed">
                Some of the selected rolls are stored at different locations. To ensure correct logistics and batch handling, rolls from different locations cannot be combined in one order.
              </p>
              <p class="text-gray-600 text-sm leading-relaxed mt-2">
                Please complete your current order or start a new order for rolls from another location.
              </p>
              <div class="mt-4 flex gap-3">
                <button onclick="this.closest('#location-error-modal').remove()" class="px-4 py-2 bg-primary-500 text-white rounded-lg cursor-pointer">
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      `;

      document.body.appendChild(modal);
    }

    showError(errorDiv, message) {
      if (!errorDiv) return;
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
      setTimeout(() => {
        errorDiv.style.display = 'none';
      }, 3000);
    }

    async fetchCartItems() {
      try {
        const response = await fetch(window.Shopify.routes.root + 'cart.js');
        const cart = await response.json();

        // Clear and rebuild cart items set
        this.cartItems.clear();
        this.cartRollLocation = null;

        cart.items.forEach(item => {
          // Add product ID to set
          this.cartItems.add(item.product_id.toString());

          // Check for roll location in line item properties
          if (item.properties && item.properties._location) {
            // This is a turf roll with location - track it
            if (!this.cartRollLocation) {
              this.cartRollLocation = item.properties._location;
            }
          }
        });

        this.saveCartItemsToStorage();
        this.updateCartButtonStates();

      } catch (error) {
        console.error('Error fetching cart:', error);
        // Fall back to localStorage
        this.loadCartItemsFromStorage();
        this.updateCartButtonStates();
      }
    }

    updateCartButtonStates() {
      const rows = this.querySelectorAll('.product-row');

      rows.forEach(row => {
        const productId = row.dataset.productId;
        const button = row.querySelector('button[data-product-id]');

        if (!button) return;

        const isInCart = this.cartItems.has(productId);
        this.updateButtonState(button, isInCart);
      });
    }

    updateButtonState(button, isInCart) {
      const buttonText = button.querySelector('.add-button-text');
      const buttonAdded = button.querySelector('.add-button-added');
      const iconPlus = button.querySelector('.icon-plus');
      const iconCheck = button.querySelector('.icon-check');

      if (isInCart) {
        button.disabled = true;
        button.classList.remove('bg-primary-500', 'lg:hover:bg-primary-600');
        button.classList.add('bg-emerald-400');
        if (buttonText) buttonText.classList.add('hidden');
        if (buttonAdded) buttonAdded.classList.remove('hidden');
        if (iconPlus) iconPlus.classList.add('hidden');
        if (iconCheck) iconCheck.classList.remove('hidden');
      } else {
        button.disabled = false;
        button.classList.add('bg-primary-500', 'lg:hover:bg-primary-600');
        button.classList.remove('bg-emerald-400');
        if (buttonText) buttonText.classList.remove('hidden');
        if (buttonAdded) buttonAdded.classList.add('hidden');
        if (iconPlus) iconPlus.classList.remove('hidden');
        if (iconCheck) iconCheck.classList.add('hidden');
      }
    }

    onCartUpdate(event) {
      // Re-fetch cart items when cart is updated elsewhere
      this.fetchCartItems();
    }

    saveCartItemsToStorage() {
      try {
        localStorage.setItem('collection_table_cart_items', JSON.stringify([...this.cartItems]));
        if (this.cartRollLocation) {
          localStorage.setItem('cart_roll_location', this.cartRollLocation);
        } else {
          localStorage.removeItem('cart_roll_location');
        }
      } catch (error) {
        console.error('Error saving to localStorage:', error);
      }
    }

    loadCartItemsFromStorage() {
      try {
        const stored = localStorage.getItem('collection_table_cart_items');
        if (stored) {
          this.cartItems = new Set(JSON.parse(stored));
        }
        const storedLocation = localStorage.getItem('cart_roll_location');
        if (storedLocation) {
          this.cartRollLocation = storedLocation;
        }
      } catch (error) {
        console.error('Error loading from localStorage:', error);
      }
    }

    async refreshCart() {
      try {
        // Fetch current cart data
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        if (!response.ok) return;

        const cart = await response.json();

        // Update header cart count bubbles
        const cartCountBubbles = document.querySelectorAll('.cart-count-bubble');
        cartCountBubbles.forEach(bubble => {
          // Show/hide bubble based on cart count
          bubble.classList.toggle('hidden', cart.item_count === 0);
          // Update count text
          const countSpan = bubble.querySelector('span[aria-hidden="true"]');
          if (countSpan) countSpan.textContent = cart.item_count;
        });

        // Update visually hidden text for accessibility
        const cartCountAccessible = document.querySelectorAll('.cart-count-bubble .visually-hidden');
        cartCountAccessible.forEach(el => {
          el.textContent = `${cart.item_count} ${cart.item_count === 1 ? 'item' : 'items'}`;
        });

        // Refresh cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
          document.documentElement.dispatchEvent(
            new CustomEvent('cart:refresh', {
              bubbles: true,
            })
          );
        }
      } catch (error) {
        console.error('Error refreshing cart:', error);
      }
    }

    async updateCollectionWithUrl(url) {
      const loadingOverlay = this.closest('.collection-product-table-wrapper').querySelector('.loading-overlay');

      if (loadingOverlay) {
        loadingOverlay.style.display = 'block';
      }

      try {
        const response = await fetch(url);
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Update the table body
        const newTableBody = doc.getElementById('ProductTableBody');
        if (newTableBody && this.tableBody) {
          this.tableBody.innerHTML = newTableBody.innerHTML;
        }

        // Update product count
        const newProductCount = doc.getElementById('ProductCount');
        const currentProductCount = document.getElementById('ProductCount');
        if (newProductCount && currentProductCount) {
          currentProductCount.innerHTML = newProductCount.innerHTML;
        }

        // Update active facets
        const newActiveFacets = doc.querySelector('.active-facets');
        const currentActiveFacets = document.querySelector('.active-facets');
        if (newActiveFacets && currentActiveFacets) {
          // Save the search badge before updating
          const searchBadge = currentActiveFacets.querySelector('.search-query-badge');
          const searchBadgeClone = searchBadge ? searchBadge.cloneNode(true) : null;

          currentActiveFacets.innerHTML = newActiveFacets.innerHTML;

          // Restore the search badge at the beginning
          if (searchBadgeClone) {
            currentActiveFacets.insertBefore(searchBadgeClone, currentActiveFacets.firstChild);

            // Re-attach event listener
            const clearSearchButton = searchBadgeClone.querySelector('#ClearSearchQuery');
            if (clearSearchButton) {
              clearSearchButton.addEventListener('click', this.handleClearSearch.bind(this));
            }
          }

          // Show/hide based on whether there are active filters
          if (newActiveFacets.classList.contains('hidden')) {
            currentActiveFacets.classList.add('hidden');
          } else {
            currentActiveFacets.classList.remove('hidden');
          }

          // Restore outlet badge visibility (markup is re-rendered hidden by the server)
          this.updateOutletBadge(this.outletFilterActive);
        }

        // Update filter form fields to match URL state
        const newForm = doc.getElementById('FacetFiltersForm');
        const currentForm = document.getElementById('FacetFiltersForm');
        if (newForm && currentForm) {
          // Update all select and input values (except search input)
          const newSelects = newForm.querySelectorAll('select, input');
          newSelects.forEach(newInput => {
            // Skip the search input - it's managed separately
            if (newInput.name === 'q') {
              return;
            }

            const currentInput = currentForm.querySelector(`[name="${newInput.name}"]`);
            if (currentInput) {
              if (currentInput.tagName === 'SELECT') {
                currentInput.value = newInput.value;
              } else if (currentInput.type === 'search' || currentInput.type === 'text') {
                currentInput.value = newInput.value;
              } else if (currentInput.type === 'number') {
                currentInput.value = newInput.value;
              }
            }
          });
        }

        // Update sort dropdown
        const newSortSelect = doc.getElementById('SortBy');
        const currentSortSelect = document.getElementById('SortBy');
        if (newSortSelect && currentSortSelect) {
          currentSortSelect.value = newSortSelect.value;
        }

        // Update load more button
        const newLoadMoreButton = doc.getElementById('LoadMoreButton');
        const currentLoadMoreButton = document.getElementById('LoadMoreButton');
        if (newLoadMoreButton && currentLoadMoreButton) {
          currentLoadMoreButton.dataset.nextUrl = newLoadMoreButton.dataset.nextUrl;
        } else if (!newLoadMoreButton && currentLoadMoreButton) {
          currentLoadMoreButton.remove();
          this.loadMoreButton = null;
        }

        // Update URL without reload
        window.history.pushState({}, '', url);

        // Ensure all product rows are visible after update
        this.showAllProductRows();

        // Re-setup event listeners
        this.setupBatchNumberLinks();
        this.setupAddToCartForms();

        // Reload all pages if they were previously loaded
        if (this.allProductsLoaded) {
          this.loadMoreButton = document.getElementById('LoadMoreButton');
          await this.loadAllRemainingProducts();
        }

        this.updateCartButtonStates();

        // Re-setup clear filter buttons
        const clearButtons = document.querySelectorAll('.active-facets a');
        clearButtons.forEach(button => {
          if (button.textContent.toLowerCase().includes('clear') || button.href.includes(window.location.pathname + '?')) {
            button.addEventListener('click', (event) => {
              event.preventDefault();
              if (button.closest('facet-remove')) {
                // Individual filter removal
                this.updateCollectionWithUrl(button.href);
              } else {
                // Clear all filters
                this.handleClearFilters();
              }
            });
          }
        });

      } catch (error) {
        console.error('Error updating collection:', error);
      } finally {
        if (loadingOverlay) {
          loadingOverlay.style.display = 'none';
        }
      }
    }
  }

  customElements.define('collection-product-table', CollectionProductTable);
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', () => {
  const collectionTable = document.querySelector('collection-product-table');
  if (collectionTable) {
    // Ensure all product rows are visible
    collectionTable.showAllProductRows();
    // Ensure cart items are loaded
    collectionTable.fetchCartItems();
  }
});
